import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Button,
  Grid,
  InputAdornment,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from "@mui/material";
import { format } from "date-fns";
import React, { useEffect, useMemo, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";

interface DetailItem {
  // Saudi format fields
  FromTo: string;
  Hrs: number;
  Lateral: number;
  Phase: string;
  Cat: string;
  MajorOP: string;
  Action: string;
  Object: string;
  Responsible: string;
  Summary: string;
  // Abu Dhabi format - just Content
  Content: string;
  // Common fields
  Rig: string;
  Date: string;
  Well: string;
  FileUrl: string;
  [key: string]: any;
}

interface Report {
  Rig: string;
  Date: string;
  Well: string;
  FileUrl: string;
  Details: DetailItem[];
  [key: string]: any;
}

interface DetailsTableProps {
  searchTerm?: string;
  highlightTerm?: string;
  hideSearch?: boolean;
  initialData?: DetailItem[];
}

const escapeRegex = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const tokenizeSearch = (value: string): string[] => {
  const trimmed = (value || "").trim().toLowerCase();
  if (!trimmed) return [];
  const tokens: string[] = [];
  const regex = /\"([^\"]+)\"|([^\s]+)/g; // "phrase" or singleWord
  let match: RegExpExecArray | null;
  while ((match = regex.exec(trimmed)) !== null) {
    const phrase = (match[1] || match[2] || "").trim();
    if (phrase.length > 0) tokens.push(phrase);
  }
  return tokens;
};

const HighlightText = ({
  text,
  searchTerm,
}: {
  text: string | number;
  searchTerm: string;
}) => {
  if (!text) return <>{text}</>;
  const tokens = tokenizeSearch(searchTerm);
  if (tokens.length === 0) return <>{text}</>;

  const pattern = new RegExp(`(${tokens.map(escapeRegex).join("|")})`, "gi");
  const parts = String(text).split(pattern);
  const tokenSet = new Set(tokens.map((t) => t.toLowerCase()));

  return (
    <>
      {parts.map((part, i) => {
        const lower = part.toLowerCase();
        if (tokenSet.has(lower)) {
          return (
            <span
              key={i}
              style={{
                backgroundColor: "#ff9800",
                color: "#000",
                padding: "0 2px",
                borderRadius: "2px",
                fontWeight: "bold",
              }}
            >
              {part}
            </span>
          );
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </>
  );
};

const DetailsTable: React.FC<DetailsTableProps> = ({
  searchTerm: externalSearchTerm,
  highlightTerm,
  hideSearch = false,
  initialData,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(100);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [detailsData, setDetailsData] = useState<{
    columns: any[];
    rows: DetailItem[];
  }>({ columns: [], rows: [] });
  const [localSearchTerm, setLocalSearchTerm] = useState("");

  const handleDateClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDateClose = () => {
    setAnchorEl(null);
  };

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date || new Date());
    handleDateClose();
  };

  const open = Boolean(anchorEl);

  useEffect(() => {
    // Detect if this is Abu Dhabi format (has Content field) or Saudi format (has FromTo field)
    const isAbuDhabiFormat = initialData?.some(item =>
      item.Content && item.Content.trim() !== '' && (!item.FromTo || item.FromTo.trim() === '')
    );

    const detailsColumns = isAbuDhabiFormat
      ? [
          // Abu Dhabi format - simple Content column (no Date field)
          { name: "Rig", width: "100px" },
          { name: "Well", width: "130px" },
          { name: "Content", flex: 1 }, // Full row content
          { name: "Actions", width: "40px" },
        ]
      : [
          // Saudi format - structured columns
          { name: "Rig", width: "100px" },
          { name: "Date", width: "170px" },
          { name: "Well", width: "130px" },
          { name: "FromTo", width: "110px" },
          { name: "Hrs", width: "80px" },
          { name: "Responsible", width: "60px" },
          { name: "Summary", flex: 1 },
          { name: "Actions", width: "40px" },
        ];

    setDetailsData({ columns: detailsColumns, rows: initialData || [] });
  }, [initialData]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const effectiveSearchTerm = externalSearchTerm || localSearchTerm;
  const effectiveHighlightTerm = highlightTerm || effectiveSearchTerm;

  const filteredRows = useMemo(() => {
    const tokens = tokenizeSearch(effectiveSearchTerm);
    if (tokens.length === 0) return detailsData.rows;
    return detailsData.rows.filter((detail) => {
      const haystack = Object.values(detail)
        .map((v) => String(v).toLowerCase())
        .join(" ");
      return tokens.every((token) => haystack.includes(token));
    });
  }, [detailsData.rows, effectiveSearchTerm]);

  return (
    <div>
      {!hideSearch && (
        <Box sx={{ mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search by multiple words (use quotes for phrases)"
                value={localSearchTerm}
                onChange={(e) => setLocalSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleDateClick}
                startIcon={<CalendarMonthIcon />}
                sx={{ height: "56px" }}
              >
                {selectedDate
                  ? format(selectedDate, "MMM dd, yyyy")
                  : "Select Date"}
              </Button>
              <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleDateClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <Box sx={{ p: 2 }}>
                  <DayPicker
                    mode="single"
                    selected={selectedDate}
                    onSelect={handleDateSelect}
                    styles={{
                      caption: { color: "#1976d2" },
                      day_selected: { backgroundColor: "#1976d2" },
                      day_today: { color: "#1976d2" },
                      day: { margin: 0 },
                    }}
                  />
                </Box>
              </Popover>
            </Grid>
          </Grid>
        </Box>
      )}
      {filteredRows.length === 0 && effectiveSearchTerm ? (
        <Alert severity="info">No details found</Alert>
      ) : (
        <Box sx={{ bgcolor: "background.paper", borderRadius: 1 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {detailsData.columns.map((column) => (
                    <TableCell
                      key={column.name}
                      style={{ width: column.width }}
                    >
                      {column.name}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((detail, index) => (
                    <TableRow key={index}>
                      {detailsData.columns.map((column) => {
                        const value = detail[column.name];
                        return (
                          <TableCell key={column.name}>
                            {column.name === "Actions" ? (
                              detail.FileUrl ? (
                                <Button
                                  variant="contained"
                                  size="small"
                                  href={detail.FileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{
                                    fontSize: "0.75rem",
                                    textTransform: "none",
                                    backgroundColor: "transparent",
                                    color: "white",
                                    "& .MuiSvgIcon-root": {
                                      color: "white",
                                    },
                                    "&:hover": {
                                      backgroundColor:
                                        "rgba(255, 255, 255, 0.1)",
                                    },
                                  }}
                                >
                                  Open Report
                                </Button>
                              ) : null
                            ) : (
                              <HighlightText
                                text={value?.toString() || ""}
                                searchTerm={effectiveHighlightTerm}
                              />
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredRows.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[100]}
          />
        </Box>
      )}
    </div>
  );
};

export default DetailsTable;
